/**
 * agent.ts
 *
 * Pure ACP protocol layer. Implements acp.Agent by translating every ACP
 * method/notification into session-bus messages and vice versa.
 *
 * Takes a DownstreamTransport (ACP → Backend) and UpstreamTransport (Backend → ACP)
 * separately. Pass the same instance for both when using a bidirectional transport
 * (NATS, WS), or different instances for mixed configurations (e.g. webhook in, NATS out).
 *
 * Zero knowledge of wire protocol — never references NATS, WebSocket, HTTP, etc.
 */

// ─── AgentConnection ──────────────────────────────────────────────────────────
// Structural interface for the subset of AgentSideConnection that PineSeedAgent
// actually calls. Accepting this interface instead of the concrete class allows
// the browser/direct path to supply an in-process proxy without a cast.

import * as acp from "@agentclientprotocol/sdk";
import { RequestError } from "@agentclientprotocol/sdk";
import { toRequestError } from "./errors";

/**
 * Structural handle returned by AgentConnection.createTerminal().
 * Mirrors the public surface of the SDK's TerminalHandle without depending on
 * its private internals. AgentSideConnection.createTerminal() satisfies this
 * structurally; BrowserTerminalHandle (sdk/terminal.ts) implements it directly.
 */
export interface TerminalHandleLike {
  readonly id: string;
  currentOutput(): Promise<acp.TerminalOutputResponse>;
  waitForExit(): Promise<acp.WaitForTerminalExitResponse>;
  kill(): Promise<acp.KillTerminalResponse>;
  release(): Promise<acp.ReleaseTerminalResponse | void>;
}

export interface AgentConnection {
  sessionUpdate(params: acp.SessionNotification): Promise<void>;
  requestPermission(
    params: acp.RequestPermissionRequest,
  ): Promise<acp.RequestPermissionResponse>;
  readTextFile(
    params: acp.ReadTextFileRequest,
  ): Promise<acp.ReadTextFileResponse>;
  writeTextFile(
    params: acp.WriteTextFileRequest,
  ): Promise<acp.WriteTextFileResponse>;
  createTerminal(
    params: acp.CreateTerminalRequest,
  ): Promise<TerminalHandleLike>;
  /**
   * Dispatches a custom tool call to the client.
   * toolName is the registered tool name; args are the LLM-provided arguments.
   * Returns the tool result payload or throws on error.
   */
  handleCustomTool(
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<Record<string, unknown>>;
}

import type {
  DownstreamTransport,
  UpstreamTransport,
  UpstreamMessage,
} from "@pineseed/transport";
import type { PromptPipeline } from "./pipeline/prompt-pipeline";
import type { SessionScope } from "./pipeline/types";
import type { UpdatePipeline } from "./pipeline/update-pipeline";
import { InProcessPromptQueue } from "./pipeline/queue";
import type { PromptQueue } from "./pipeline/queue";
import { uuidv7 } from "uuidv7";

/**
 * Resolves the full session scope from sessionId + workspaceId.
 * Called once at session open time — before any prompts are processed.
 * Closed over apiUrl + apiKey at the entrypoint boundary.
 */
export type ScopeResolver = (
  sessionId: string,
  workspaceId: string,
) => Promise<SessionScope>;

/**
 * Called after session/new or session/load with the resolved sessionId, workspaceId,
 * and pre-fetched SessionScope.
 * participantId is closed over in the entrypoint factory closure.
 */
export type PromptPipelineFactory = (
  sessionId: string,
  workspaceId: string,
  scope: SessionScope,
) => PromptPipeline;

// ─── Tool result enrichment constants ──────────────────────────────────────

/** Default result for void-returning tools — signals success to the LLM. */
const VOID_SUCCESS: Record<string, unknown> = { _meta: { result: "success" } };

/**
 * Per-tool result enrichers keyed by upstream message type.
 * Return the enriched result; receives the raw result from AgentConnection.
 * Add entries here to override default enrichment for specific tool types.
 */
const TOOL_RESULT_ENRICHERS: Record<
  string,
  (result: Record<string, unknown>) => Record<string, unknown>
> = {
  // Example: fs_read_request result already carries { content: string } — no enrichment needed.
};

export class PineSeedAgent implements acp.Agent {
  private connection: AgentConnection;
  private downstream: DownstreamTransport;
  private clientCapabilities: acp.ClientCapabilities = {};

  // Pending turns per session (FIFO): each prompt() pushes an entry,
  // each upstream "stop" shifts the first. Supports concurrent prompts.
  private pendingTurns = new Map<
    string,
    Array<{
      resolve: (stopReason: acp.StopReason) => void;
      reject: (err: unknown) => void;
      ac: AbortController;
    }>
  >();

  // Pending correlated promises: key = correlationId
  // Used for: set_mode_response, set_config_response, list_sessions_response
  private pendingCorrelated = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: unknown) => void;
    }
  >();

  // TerminalHandleLike keyed by terminalId — managed here so backend only deals
  // with opaque string IDs, not SDK objects
  private terminals = new Map<string, TerminalHandleLike>();

  private promptPipeline?: PromptPipeline;
  private promptQueue?: PromptQueue;
  private promptPipelineFactory?: PromptPipelineFactory;
  private scopeResolver?: ScopeResolver;
  private updatePipeline?: UpdatePipeline;
  private participantId?: string;
  private location: string;
  private heartbeatTimer: ReturnType<typeof setInterval> | undefined =
    undefined;

  constructor(
    connection: AgentConnection,
    downstream: DownstreamTransport,
    upstream: UpstreamTransport,
    opts: {
      promptPipelineFactory?: PromptPipelineFactory;
      scopeResolver?: ScopeResolver;
      updatePipeline?: UpdatePipeline;
      /** Stable IDE client identifier — used as processId for tool heartbeats. */
      participantId?: string;
      /** Provider location label for tool registration (e.g. "d-macbook-pro"). */
      location: string;
      /** Called by the transport when the connection drops mid-session. */
      onTransportClose?: (rejectTurns: () => void) => void;
    },
  ) {
    this.connection = connection;
    this.downstream = downstream;
    if (opts.promptPipelineFactory != null) {
      this.promptPipelineFactory = opts.promptPipelineFactory;
    }
    if (opts.scopeResolver != null) {
      this.scopeResolver = opts.scopeResolver;
    }
    if (opts.updatePipeline != null) {
      this.updatePipeline = opts.updatePipeline;
    }
    if (opts.participantId != null) {
      this.participantId = opts.participantId;
    }
    this.location = opts.location;
    if (opts.onTransportClose != null) {
      opts.onTransportClose(() => {
        this.promptQueue?.dispose();
        this.rejectAllPendingTurns();
        if (this.heartbeatTimer != null) {
          clearInterval(this.heartbeatTimer);
          this.heartbeatTimer = undefined;
        }
      });
    }
    // Single entry point for ALL upstream messages from ALL sessions
    upstream.onMessage((msg) => this.handleUpstream(msg));
  }

  /**
   * Rejects all in-flight prompt turns with a transport-closed error.
   * Called when the underlying transport drops unexpectedly so prompt()
   * surfaces a JSON-RPC error instead of hanging forever or crashing.
   */
  // ─── Tool result enrichment ───────────────────────────────────────────────
  // Enriches tool results before sending downstream.
  // Per-tool overrides take precedence; void results default to success metadata.
  // Extend by adding toolType-keyed entries to TOOL_RESULT_ENRICHERS.

  private enrichToolResult(
    toolType: string,
    result: Record<string, unknown>,
  ): Record<string, unknown> {
    const enricher = TOOL_RESULT_ENRICHERS[toolType];
    if (enricher) return enricher(result);
    if (Object.keys(result).length === 0) return VOID_SUCCESS;
    return result;
  }

  rejectAllPendingTurns(): void {
    const err = {
      code: -32603,
      message:
        "Transport closed — server unreachable. Re-initialize to reconnect.",
    };
    for (const [sessionId, queue] of this.pendingTurns) {
      this.pendingTurns.delete(sessionId);
      for (const turn of queue) turn.reject(err);
    }
  }

  // ─── Upstream handler: Backend → ACP ───────────────────────────────────────

  private async handleUpstream(msg: UpstreamMessage): Promise<void> {
    switch (msg.type) {
      // Forward stream chunk / tool call / plan / mode / config / command updates
      case "update": {
        const renderedUpdate = this.updatePipeline
          ? await this.updatePipeline.render(msg.update)
          : msg.update;
        await this.connection.sessionUpdate({
          sessionId: msg.sessionId,
          update: renderedUpdate,
        });
        break;
      }

      // Turn complete — resolve the oldest awaited prompt() promise (FIFO)
      case "stop": {
        const queue = this.pendingTurns.get(msg.sessionId);
        if (queue?.length) {
          const turn = queue.shift()!;
          if (queue.length === 0) this.pendingTurns.delete(msg.sessionId);
          turn.resolve(msg.stopReason);
        }
        break;
      }

      // Permission round-trip: backend needs user decision → editor → back downstream
      case "permission_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "permission_response",
          () =>
            this.connection.requestPermission({
              ...msg.req,
              sessionId: msg.sessionId,
            }),
        );
        break;
      }

      // File system round-trips (editor has the live buffer, backend needs contents)
      case "fs_read_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "fs_read_response",
          () =>
            this.connection.readTextFile({
              ...msg.req,
              sessionId: msg.sessionId,
            }),
        );
        break;
      }
      case "fs_write_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "fs_write_response",
          async () =>
            this.enrichToolResult(
              "fs_write_request",
              await this.connection.writeTextFile({
                ...msg.req,
                sessionId: msg.sessionId,
              }),
            ),
        );
        break;
      }

      // Terminal round-trips — SDK returns a TerminalHandle; we store it by id
      // and only pass the opaque terminalId string across the bus
      case "terminal_create_request": {
        await this.safeRoundTripRaw(
          msg.correlationId,
          "terminal_create_response",
          async () => {
            const handle = await this.connection.createTerminal({
              ...msg.req,
              sessionId: msg.sessionId,
            });
            this.terminals.set(handle.id, handle);
            return { terminalId: handle.id };
          },
        );
        break;
      }
      case "terminal_output_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "terminal_output_response",
          () => {
            const handle = this.terminals.get(msg.terminalId);
            if (!handle) throw new Error(`Unknown terminal: ${msg.terminalId}`);
            return handle.currentOutput();
          },
        );
        break;
      }
      case "terminal_exit_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "terminal_exit_response",
          () => {
            const handle = this.terminals.get(msg.terminalId);
            if (!handle) throw new Error(`Unknown terminal: ${msg.terminalId}`);
            return handle.waitForExit();
          },
        );
        break;
      }
      case "terminal_kill_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "terminal_kill_response",
          async () => {
            const handle = this.terminals.get(msg.terminalId);
            if (!handle) throw new Error(`Unknown terminal: ${msg.terminalId}`);
            return this.enrichToolResult(
              "terminal_kill_request",
              await handle.kill(),
            );
          },
        );
        break;
      }
      case "terminal_release_request": {
        await this.safeRoundTripResult(
          msg.correlationId,
          "terminal_release_response",
          async () => {
            const handle = this.terminals.get(msg.terminalId);
            if (!handle) throw new Error(`Unknown terminal: ${msg.terminalId}`);
            await handle.release();
            this.terminals.delete(msg.terminalId);
            return this.enrichToolResult("terminal_release_request", {});
          },
        );
        break;
      }

      // Custom tool call from harness — dispatch to client, return result downstream.
      case "custom_tool_request": {
        let result: Record<string, unknown>;
        let isError = false;
        let errorMessage: string | undefined;
        try {
          result = await this.connection.handleCustomTool(
            msg.toolName,
            msg.arguments,
          );
        } catch (err) {
          result = {};
          isError = true;
          errorMessage = err instanceof Error ? err.message : String(err);
        }
        await this.downstream.send({
          type: "custom_tool_response",
          correlationId: msg.correlationId,
          result,
          isError,
          ...(errorMessage != null ? { errorMessage } : {}),
        });
        break;
      }

      // Heartbeat interval: edge has registered tools and specifies the liveness interval.
      // Agent starts (or restarts) a timer to send heartbeat downstream.
      case "heartbeat_interval": {
        if (!this.participantId) break;
        if (this.heartbeatTimer != null) clearInterval(this.heartbeatTimer);
        const participantId = this.participantId;
        this.heartbeatTimer = setInterval(() => {
          this.downstream
            .send({ type: "heartbeat", participantId })
            .catch((err) =>
              console.error("[agent] heartbeat send failed:", err),
            );
        }, msg.intervalMs);
        break;
      }

      // Correlated responses to ACP-initiated requests
      case "set_mode_response":
      case "set_config_response":
      case "list_sessions_response": {
        const pending = this.pendingCorrelated.get(msg.correlationId);
        if (pending) {
          this.pendingCorrelated.delete(msg.correlationId);
          pending.resolve(msg.result);
        }
        break;
      }
    }
  }

  // ─── Round-trip error containment ────────────────────────────────────────
  // Executes op(), sends the result downstream as `type` message.
  // On throw: logs the error and sends an error-shaped result so the harness
  // is never left waiting on a pending correlationId.

  // Wraps op() result under { result } — use for all standard round-trips.
  private async safeRoundTripResult(
    correlationId: string,
    type: string,
    op: () => Promise<unknown>,
  ): Promise<void> {
    try {
      const result = await op();
      await this.downstream.send({ type, correlationId, result } as any);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error(
        `[agent] round-trip error type=${type} correlationId=${correlationId}:`,
        err,
      );
      await this.downstream.send({
        type,
        correlationId,
        result: { isError: true, errorMessage },
      } as any);
    }
  }

  // Spreads op() result at top level — use for responses with non-result top-level fields (e.g. terminal_create_response { terminalId }).
  private async safeRoundTripRaw(
    correlationId: string,
    type: string,
    op: () => Promise<Record<string, unknown>>,
  ): Promise<void> {
    try {
      const fields = await op();
      await this.downstream.send({ type, correlationId, ...fields } as any);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error(
        `[agent] round-trip error type=${type} correlationId=${correlationId}:`,
        err,
      );
      await this.downstream.send({
        type,
        correlationId,
        result: { isError: true, errorMessage },
      } as any);
    }
  }

  // ─── Correlated request helper ──────────────────────────────────────────────

  private awaitCorrelated<T>(correlationId: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.pendingCorrelated.set(correlationId, {
        resolve: resolve as (v: unknown) => void,
        reject,
      });
    });
  }

  // ─── ACP interface: Editor → ACP ───────────────────────────────────────────

  async initialize(
    params: acp.InitializeRequest,
  ): Promise<acp.InitializeResponse> {
    this.clientCapabilities = params.clientCapabilities ?? {};
    try {
      return await this.downstream.getCapabilities();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw RequestError.internalError(undefined, `Agent backend unavailable: ${message}`);
    }
  }

  async authenticate(
    _params: acp.AuthenticateRequest,
  ): Promise<acp.AuthenticateResponse> {
    // Most backends are no-op; transports that require auth override getCapabilities()
    // to advertise authMethods and handle auth at the connection level
    return {};
  }

  async newSession(
    params: acp.NewSessionRequest,
  ): Promise<acp.NewSessionResponse> {
    try {
      const sessionId = crypto.randomUUID();
      // clientCapabilities and location forwarded so backend can gate on fs/terminal support
      // and register tools with the correct provider location label.
      // _location is a PineSeed extension carried in params; cast required as ACP SDK
      // does not define it.
      const paramsWithLocation = { ...params, _location: this.location } as acp.NewSessionRequest;
      const rest = await this.downstream.openSession(
        sessionId,
        paramsWithLocation,
        this.clientCapabilities,
      );
      // workspaceId is returned in SessionInitState by the server (resolved from the API key).
      const workspaceId = (rest as any).workspaceId as string | undefined;
      await this.#instantiatePipeline(sessionId, workspaceId, "newSession");
      return { sessionId, ...rest };
    } catch (err) {
      throw toRequestError(err);
    }
  }

  async loadSession(
    params: acp.LoadSessionRequest,
  ): Promise<acp.LoadSessionResponse> {
    try {
      if (!this.downstream.loadSession)
        throw new Error("session/load not supported by this transport");
      const paramsWithLocation = { ...params, _location: this.location } as acp.LoadSessionRequest;
      const result = await this.downstream.loadSession(
        params.sessionId,
        paramsWithLocation,
        this.clientCapabilities,
      );
      // workspaceId is carried in load_session_response result alongside SESSION_INIT_STATE
      const workspaceId = (result as any).workspaceId as string | undefined;
      await this.#instantiatePipeline(
        params.sessionId,
        workspaceId,
        "loadSession",
      );
      return result;
    } catch (err) {
      throw toRequestError(err);
    }
  }

  async #instantiatePipeline(
    sessionId: string,
    workspaceId: string | undefined,
    caller: string,
  ): Promise<void> {
    if (!this.promptPipelineFactory) return;
    if (!workspaceId) {
      throw new Error(
        `${caller}: session_opened missing workspaceId — server must include it in SessionInitState`,
      );
    }
    if (!this.scopeResolver) {
      throw new Error(
        `${caller}: scopeResolver is required when promptPipelineFactory is set`,
      );
    }
    const scope = await this.scopeResolver(sessionId, workspaceId);
    this.promptPipeline = this.promptPipelineFactory(
      sessionId,
      workspaceId,
      scope,
    );
    // Dispose any previous queue before creating a new one (session switch).
    this.promptQueue?.dispose();
    const queue = new InProcessPromptQueue();
    this.promptQueue = queue;
    // Capture pipeline + downstream for the drain loop closure.
    const pipeline = this.promptPipeline;
    const downstream = this.downstream;
    // Start drain loop — runs concurrently, processes tasks sequentially.
    (async () => {
      for await (const task of queue.drain()) {
        try {
          const { messageId } = await pipeline.process(task);
          // console.warn(`[agent:drain] sending prompt_ready sessionId=${task.sessionId} messageId=${messageId}`);
          await downstream.send({
            type: "prompt_ready",
            sessionId: task.sessionId,
            messageId,
          });
          // console.warn(`[agent:drain] prompt_ready sent`);
        } catch (err) {
          console.error(
            `[agent] drain task error sessionId=${task.sessionId} messageId=${task.messageId}: ${err}`,
          );
        }
      }
    })();
  }

  async listSessions(
    params: acp.ListSessionsRequest,
  ): Promise<acp.ListSessionsResponse> {
    // Goes through the bus: downstream list_sessions → upstream list_sessions_response
    const correlationId = crypto.randomUUID();
    const promise =
      this.awaitCorrelated<acp.ListSessionsResponse>(correlationId);
    await this.downstream.send({
      type: "list_sessions",
      correlationId,
      params,
    });
    return promise;
  }

  async prompt(params: acp.PromptRequest): Promise<acp.PromptResponse> {
    // console.error(
    //   `[agent] prompt called sessionId=${params.sessionId} blocks=${params.prompt.length}`,
    // );
    const ac = new AbortController();

    const stopReason = await new Promise<acp.StopReason>((resolve, reject) => {
      const queue = this.pendingTurns.get(params.sessionId) ?? [];
      queue.push({ resolve, reject, ac });
      this.pendingTurns.set(params.sessionId, queue);

      if (this.promptQueue && params.prompt.length > 0) {
        // Enqueue — the drain loop processes sequentially and sends prompt_ready.
        const messageId = uuidv7();
        try {
          // console.error(
          //   `[agent] enqueuing sessionId=${params.sessionId} messageId=${messageId}`,
          // );
          this.promptQueue.enqueue({
            messageId,
            sessionId: params.sessionId,
            params,
          });
        } catch (err) {
          console.error(
            `[agent] enqueue error sessionId=${params.sessionId}: ${err}`,
          );
          reject(err);
        }
      } else {
        // No pipeline — send full prompt downstream directly.
        this.downstream
          .send({ type: "prompt", sessionId: params.sessionId, params })
          .catch(reject);
      }
    });

    return { stopReason };
  }

  async cancel(params: acp.CancelNotification): Promise<void> {
    const queue = this.pendingTurns.get(params.sessionId);
    queue?.[0]?.ac.abort();
    await this.downstream.send({ type: "cancel", sessionId: params.sessionId });
  }

  async setSessionMode(
    params: acp.SetSessionModeRequest,
  ): Promise<acp.SetSessionModeResponse> {
    const correlationId = crypto.randomUUID();
    const promise =
      this.awaitCorrelated<acp.SetSessionModeResponse>(correlationId);
    // correlationId is a bus-level field — never touches ACP _meta
    await this.downstream.send({
      type: "set_mode",
      sessionId: params.sessionId,
      correlationId,
      params,
    });
    return promise;
  }

  async setSessionConfigOption(
    params: acp.SetSessionConfigOptionRequest,
  ): Promise<acp.SetSessionConfigOptionResponse> {
    const correlationId = crypto.randomUUID();
    const promise =
      this.awaitCorrelated<acp.SetSessionConfigOptionResponse>(correlationId);
    await this.downstream.send({
      type: "set_config",
      sessionId: params.sessionId,
      correlationId,
      params,
    });
    return promise;
  }
}
